/*
  La Matcha — search dialog. Loaded by sections/search-dialog.liquid.

  <search-panel> sits inside dialog#SearchDialog and shows one of three views
  ([data-search-view]): "idle" (popular content), "results" (the predictive-search section,
  fetched from the Predictive Search API) and "empty" (no-result message).

  The form works without this file: Enter submits it to the search results page.

  Keyboard: Arrow Down / Arrow Up move through the results ([data-search-item] and product rows),
  Arrow Up on the first result returns to the field, Escape closes the dialog (native <dialog>).
*/
(() => {
  const LM = (window.LM = window.LM || {});
  const parser = new DOMParser();

  /* Lower case without Turkish diacritics. Keeps the length of the text, so positions found in
     the folded text can be used on the original. */
  const fold = (text) =>
    String(text || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c');

  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.live = this.querySelector('[data-live-region]');
      this.views = {
        idle: this.querySelector('[data-search-view="idle"]'),
        results: this.querySelector('[data-search-view="results"]'),
        empty: this.querySelector('[data-search-view="empty"]'),
      };
      if (!this.input || !this.views.results) return;

      this.cache = new Map();
      this.term = '';
      try {
        this.suggestions = JSON.parse(this.querySelector('[data-search-suggestion-source]').textContent)
          .map((text) => String(text).trim())
          .filter(Boolean);
      } catch (error) {
        this.suggestions = [];
      }

      const run = () => this.search();
      const later = LM.debounce ? LM.debounce(run, 220) : run;
      this.input.addEventListener('input', () => {
        this.syncClear();
        if (this.input.value.trim()) later();
        else run();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          if (!this.input.value.trim()) event.preventDefault();
        });
      }

      this.onOpen = (event) => {
        if (!event.detail || event.detail.dialog !== this.dialog) return;
        window.setTimeout(() => this.input.focus({ preventScroll: true }), 30);
      };
      document.addEventListener('lm:dialog:open', this.onOpen);
      this.syncClear();
    }

    disconnectedCallback() {
      document.removeEventListener('lm:dialog:open', this.onOpen);
      if (this.controller) this.controller.abort();
    }

    syncClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    setLoading(state) {
      this.classList.toggle('is-loading', state);
      if (state) this.views.results.setAttribute('aria-busy', 'true');
      else this.views.results.removeAttribute('aria-busy');
    }

    show(name) {
      if (this.getAttribute('data-state') === name) return;
      Object.entries(this.views).forEach(([key, el]) => {
        if (el) el.hidden = key !== name;
      });
      this.setAttribute('data-state', name);
      const body = this.querySelector('.overlay__body');
      if (body) body.scrollTop = 0;
    }

    announce(message) {
      if (!this.live) return;
      window.clearTimeout(this.liveTimer);
      this.live.textContent = '';
      if (!message) return;
      this.liveTimer = window.setTimeout(() => {
        this.live.textContent = message;
      }, 100);
    }

    async search() {
      const term = this.input.value.trim();
      this.term = term;
      if (this.controller) this.controller.abort();
      if (!term) {
        this.setLoading(false);
        this.show('idle');
        this.announce('');
        return;
      }
      if (this.cache.has(term)) {
        this.render(term, this.cache.get(term));
        return;
      }
      const base = LM.routes && LM.routes.predictiveSearch;
      if (!base) return;
      this.controller = new AbortController();
      this.setLoading(true);
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,article,page,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(`${base}?${params.toString()}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        this.cache.set(term, html);
        if (this.term === term) this.render(term, html);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Predictive search is unavailable (for instance an unsupported storefront language).
        // The current view stays and Enter still leads to the results page.
        if (this.term === term) this.setLoading(false);
      }
    }

    render(term, html) {
      const root = parser.parseFromString(html, 'text/html').querySelector('[data-predictive-search]');
      this.setLoading(false);
      if (!root) return;
      const count = parseInt(root.getAttribute('data-count'), 10) || 0;
      if (count) {
        this.views.results.innerHTML = root.outerHTML;
        this.fillSuggestions(term);
        this.show('results');
      } else {
        const heading = this.querySelector('[data-search-empty-heading]');
        if (heading) heading.textContent = (heading.getAttribute('data-template') || '').replace('[terms]', term);
        this.show('empty');
      }
      this.announce(root.getAttribute('data-status') || '');
    }

    /* Shopify returns query suggestions for English storefronts only. Without them the list is
       built from the suggestion terms of the section, matched without Turkish diacritics. */
    fillSuggestions(term) {
      const results = this.views.results;
      const group = results.querySelector('[data-search-suggestions]');
      const list = group && group.querySelector('[data-search-suggestions-list]');
      const template = this.querySelector('[data-search-suggestion-template]');
      if (group && list && template && !list.children.length) {
        const needle = fold(term);
        const hits = this.suggestions
          .filter((text) => {
            const folded = fold(text);
            return folded.includes(needle) && folded !== needle;
          })
          .slice(0, 4);
        hits.forEach((text) => {
          const item = template.content.firstElementChild.cloneNode(true);
          const link = item.querySelector('a');
          const label = link.querySelector('span');
          const at = fold(text).indexOf(needle);
          const match = document.createElement('b');
          match.textContent = text.slice(at, at + needle.length);
          label.append(text.slice(0, at), match, text.slice(at + needle.length));
          link.setAttribute('href', `${(LM.routes && LM.routes.search) || '/search'}?q=${encodeURIComponent(text)}&options%5Bprefix%5D=last`);
          link.setAttribute('data-search-term', text);
          list.appendChild(item);
        });
        group.hidden = !hits.length;
      }
      // A first column without content gives its space to the products.
      const column = group ? group.closest('.search-col') : null;
      if (column) {
        column.hidden = ![...column.children].some((child) => !child.hidden);
        const grid = column.closest('.search-results');
        if (grid) grid.classList.toggle('search-results--single', column.hidden);
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-search-clear]')) {
        this.input.value = '';
        this.syncClear();
        this.search();
        this.input.focus();
        return;
      }
      const chip = event.target.closest('[data-search-term]');
      if (!chip || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.input.value = chip.getAttribute('data-search-term') || '';
      this.syncClear();
      this.input.focus();
      this.search();
    }

    onKeydown(event) {
      // A search field with text swallows Escape to clear itself; here Escape always closes.
      if (event.key === 'Escape' && event.target === this.input && this.input.value && this.dialog && LM.closeDialog) {
        event.preventDefault();
        LM.closeDialog(this.dialog);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = [...this.querySelectorAll('[data-search-item], .mini__text')].filter(
        (el) => el.getClientRects().length > 0 && el.getAttribute('aria-disabled') !== 'true'
      );
      if (!items.length) return;
      const active = document.activeElement;
      const index = items.indexOf(active);
      const onInput = active === this.input;
      if (!onInput && index === -1) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = items[index + 1];
        if (next) next.focus();
      } else if (!onInput) {
        event.preventDefault();
        if (index === 0) this.input.focus();
        else items[index - 1].focus();
      }
    }
  }

  if (!customElements.get('search-panel')) customElements.define('search-panel', SearchPanel);

  /* ---------- Theme editor: open the dialog while its section or one of its blocks is selected ---------- */
  if (LM.designMode) {
    let selected = null;
    const dialogIn = (target) => (target instanceof Element ? target.querySelector('#SearchDialog') : null);
    const show = (el, instant) => {
      if (instant) {
        el.setAttribute('data-no-animation', '');
        window.setTimeout(() => el.removeAttribute('data-no-animation'), 600);
      }
      if (LM.closeAllDialogs) LM.closeAllDialogs(el);
      if (LM.openDialog) LM.openDialog(el);
    };

    document.addEventListener('shopify:section:select', (event) => {
      const el = dialogIn(event.target);
      if (!el) return;
      selected = event.detail.sectionId;
      show(el, !!event.detail.load);
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const el = dialogIn(event.target);
      if (!el) return;
      selected = null;
      if (LM.closeDialog) LM.closeDialog(el);
    });

    document.addEventListener('shopify:section:load', (event) => {
      const el = dialogIn(event.target);
      if (el && selected === event.detail.sectionId) show(el, true);
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('has-dialog');
    });

    document.addEventListener('shopify:block:select', (event) => {
      const el = event.target instanceof Element ? event.target.closest('#SearchDialog') : null;
      if (!el) return;
      show(el, el.open);
      event.target.scrollIntoView({ block: 'nearest' });
    });
  }
})();
